import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { useUser } from '../../app/UserContext';
import { getCurrentMonthKey } from '../../domain/workouts/types';
import {
  buildLeaderboard,
  filterBySex,
  formatMonthKey,
  getPreviousMonthKey,
  getNextMonthKey,
  scopedBest,
  scopedValue,
  TOTAL_SCOPE,
} from '../../domain/leaderboard/types';
import type {
  LeaderboardEntry,
  LeaderboardMode,
  LeaderboardScope,
  SexFilter,
} from '../../domain/leaderboard/types';
import { formatLoadAndReps } from '../../domain/workouts/describeLift';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../domain/scoring/categories';
import { getActiveExercises } from '../../domain/scoring/exerciseCatalog';
import { getLeaderboardEntries } from '../../infrastructure/repositories/leaderboardRepository';
import { UserHistoryModal } from './UserHistoryModal';
import { useSearchParams } from 'react-router-dom';
import { computeSocialAwards } from '../../domain/awards/computeAwards';
import { AwardsView } from '../awards/AwardsView';
import '../awards/Awards.css';
import { useAwardsData } from '../awards/useAwardsData';
import './LeaderboardPage.css';

const SEX_OPTIONS: { id: SexFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'male', label: 'Men' },
  { id: 'female', label: 'Women' },
];

/**
 * Decodes a `<select>` value into a scope. Option values must be strings, so
 * categories are prefixed "c:" and single lifts "e:" - the two id spaces are
 * otherwise indistinguishable. Anything else means the All board.
 */
function valueToScope(value: string): LeaderboardScope {
  if (value.startsWith('c:')) {
    const category = CATEGORY_ORDER.find((c) => c === value.slice(2));
    if (category) return { kind: 'category', category };
  }
  if (value.startsWith('e:')) return { kind: 'exercise', exerciseId: value.slice(2) };
  return TOTAL_SCOPE;
}

/** The month before the given one: "2026-08" -> "2026-07". */
function priorMonth(monthKey: string): string {
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}

export function LeaderboardPage() {
  const { user } = useUser();
  // The feed's recap card links here with ?awards=YYYY-MM, so a reader lands on
  // the month they tapped, already in the award view.
  const [searchParams] = useSearchParams();
  const linkedMonth = searchParams.get('awards');
  const [monthKey, setMonthKey] = useState(linkedMonth ?? getCurrentMonthKey());
  const [showAwards, setShowAwards] = useState(Boolean(linkedMonth));
  const [mode, setMode] = useState<LeaderboardMode>('points');
  const [sexFilter, setSexFilter] = useState<SexFilter>('all');
  const [scopeValue, setScopeValue] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);

  const [rawEntries, setRawEntries] = useState<LeaderboardEntry[]>([]);
  const [boardLoading, setBoardLoading] = useState(true);

  const exercises = useMemo(() => getActiveExercises(), []);
  const scope = useMemo(() => valueToScope(scopeValue), [scopeValue]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setBoardLoading(true);
      try {
        const fetched = await getLeaderboardEntries(monthKey);
        if (!cancelled) setRawEntries(fetched);
      } catch (error) {
        console.error('Failed to load leaderboard', error);
        if (!cancelled) setRawEntries([]);
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [monthKey]);

  // Filter before ranking, so ranks read 1..n within what is actually shown.
  // All three filters are a pure re-sort of loaded data - nothing refetches.
  const entries = useMemo(
    () => buildLeaderboard(filterBySex(rawEntries, sexFilter), mode, scope),
    [rawEntries, sexFilter, mode, scope],
  );

  const handlePrevMonth = useCallback(() => setMonthKey((p) => getPreviousMonthKey(p)), []);
  const handleNextMonth = useCallback(() => setMonthKey((p) => getNextMonthKey(p)), []);

  const isCurrentMonth = monthKey === getCurrentMonthKey();

  // Only read the whole log once somebody actually asks for awards - the board
  // itself does not need it, and most visits never toggle.
  const { data: awardsData, isLoading: awardsLoading } = useAwardsData(showAwards);
  const monthAwards = useMemo(() => {
    if (!awardsData || isCurrentMonth) return undefined;
    const inMonth = (key: string) => awardsData.workouts.filter((w) => w.monthKey === key);
    return computeSocialAwards(inMonth(monthKey), inMonth(priorMonth(monthKey)), awardsData.users);
  }, [awardsData, monthKey, isCurrentMonth]);
  const currentUserEntry = entries.find((e) => e.userId === user?.id);
  // Your own rank pinned to the bottom is about the board; it has no meaning
  // over a list of awards.
  const showSticky = !showAwards && currentUserEntry && (currentUserEntry.rank ?? 0) > 5;

  const unit = mode === 'points' ? 'pts' : 'lb';

  const emptyMessage =
    sexFilter !== 'all'
      ? 'Nobody matching that filter has logged this yet.'
      : scope.kind === 'total'
        ? 'No lifts logged this month.'
        : 'Nobody has logged this one yet this month.';

  return (
    <Layout>
      <div className="leaderboard">
        <div className="leaderboard__header">
          <h1 className="leaderboard__title">Leaderboard</h1>

          <div className="leaderboard__month-nav">
            <button
              className="leaderboard__nav-btn"
              onClick={handlePrevMonth}
              aria-label="Previous month"
            >
              {'‹'}
            </button>
            <h2 className="leaderboard__month-label">{formatMonthKey(monthKey)}</h2>
            <button
              className="leaderboard__nav-btn"
              onClick={handleNextMonth}
              disabled={isCurrentMonth}
              aria-label="Next month"
            >
              {'›'}
            </button>
          </div>

          {/* Three bubbles on one row. The lift one flexes; the other two
                  are sized to their own widest option so the lift gets the rest. */}
          <div className="leaderboard__filters">
            <div
              className={`leaderboard__bubble leaderboard__bubble--lift ${showAwards ? 'leaderboard__bubble--muted' : ''}`}
            >
              <label className="sr-only" htmlFor="leaderboard-scope">
                Lift
              </label>
              <select
                id="leaderboard-scope"
                value={scopeValue}
                onChange={(e) => setScopeValue(e.target.value)}
              >
                <option value="all">All lifts</option>
                {/* Grouped by category, each group led by the whole category. */}
                {CATEGORY_ORDER.map((category) => (
                  <optgroup key={category} label={CATEGORY_LABELS[category]}>
                    <option value={`c:${category}`}>All {CATEGORY_LABELS[category]}</option>
                    {exercises
                      .filter((ex) => ex.category === category)
                      .map((ex) => (
                        <option key={ex.id} value={`e:${ex.id}`}>
                          {ex.displayName}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div
              className={`leaderboard__bubble leaderboard__bubble--gender ${showAwards ? 'leaderboard__bubble--muted' : ''}`}
            >
              <label className="sr-only" htmlFor="leaderboard-gender">
                Gender
              </label>
              <select
                id="leaderboard-gender"
                value={sexFilter}
                onChange={(e) => setSexFilter(e.target.value as SexFilter)}
              >
                {SEX_OPTIONS.map(({ id, label }) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div
              className={`leaderboard__bubble leaderboard__bubble--metric ${showAwards ? 'leaderboard__bubble--muted' : ''}`}
            >
              <label className="sr-only" htmlFor="leaderboard-metric">
                Ranked by
              </label>
              <select
                id="leaderboard-metric"
                value={mode}
                onChange={(e) => setMode(e.target.value as LeaderboardMode)}
              >
                <option value="points">Points</option>
                <option value="max1rm">1RM</option>
              </select>
            </div>

            {/* The filters do not apply to awards, so they dim rather than
                vanish - hiding them collapses the row and makes it jump. */}
            <button
              type="button"
              className={`leaderboard__trophy trophy-toggle ${showAwards ? 'trophy-toggle--on' : ''}`}
              onClick={() => setShowAwards((on) => !on)}
              aria-pressed={showAwards}
              aria-label={showAwards ? 'Show the leaderboard' : 'Show this month&rsquo;s awards'}
            >
              🏆
            </button>
          </div>
        </div>

        <div className="leaderboard__content">
          {showAwards ? (
            <AwardsView
              monthKey={monthKey}
              awards={monthAwards}
              isCurrentMonth={isCurrentMonth}
              isLoading={awardsLoading}
              lastFinishedMonthKey={priorMonth(monthKey)}
              onGoToLastFinished={() => setMonthKey(priorMonth(monthKey))}
            />
          ) : boardLoading ? (
            <div className="leaderboard__loading">
              <div className="leaderboard__pulse">Loading...</div>
            </div>
          ) : entries.length === 0 ? (
            <div className="leaderboard__empty">{emptyMessage}</div>
          ) : (
            <ol className="leaderboard__list">
              {entries.map((entry) => {
                const best = scopedBest(entry, scope);
                const rank = entry.rank ?? 0;
                return (
                  <li key={entry.userId}>
                    <button
                      type="button"
                      className={`board-row ${entry.userId === user?.id ? 'board-row--mine' : ''}`}
                      onClick={() => setSelectedUser({ id: entry.userId, name: entry.displayName })}
                      aria-label={`View ${entry.displayName}'s history`}
                    >
                      <span className="board-row__main">
                        <span
                          className={`board-row__rank ${rank <= 3 ? `board-row__rank--top board-row__rank--${rank}` : ''}`}
                        >
                          {rank}
                        </span>
                        <span className="board-row__name">
                          {entry.displayName}
                          {scope.kind === 'total' && entry.isIncomplete && (
                            <span
                              className="board-row__inc"
                              title="Missing at least one category this month"
                            >
                              INC
                            </span>
                          )}
                        </span>
                        <span className="board-row__score">
                          {Math.round(scopedValue(entry, mode, scope))}
                          <span className="board-row__unit">{unit}</span>
                        </span>
                      </span>

                      <span className="board-row__detail">
                        {scope.kind === 'total'
                          ? CATEGORY_ORDER.map((category) => {
                              const value =
                                mode === 'points'
                                  ? entry.bests[category]?.points
                                  : entry.bests[category]?.normalized1RmLb;
                              return (
                                <span key={category}>
                                  {CATEGORY_LABELS[category]}{' '}
                                  {value === undefined ? '—' : Math.round(value)}
                                </span>
                              );
                            })
                          : best && (
                              <>
                                {scope.kind === 'category' && `${best.exerciseName} · `}
                                {formatLoadAndReps(best)}
                              </>
                            )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {showSticky && currentUserEntry && (
          <div className="leaderboard__sticky-user">
            <div className="leaderboard__sticky-rank">#{currentUserEntry.rank}</div>
            <div className="leaderboard__sticky-name">You</div>
            <div className="leaderboard__sticky-score">
              {Math.round(scopedValue(currentUserEntry, mode, scope))} {unit}
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserHistoryModal
          userId={selectedUser.id}
          displayName={selectedUser.name}
          initialMonthKey={monthKey}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </Layout>
  );
}
